import InboxIcon from '@mui/icons-material/Inbox';
import { MenuList, Typography, Box, CircularProgress } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import _ from 'lodash';
import { useEffect } from 'react';
import type { FC } from 'react';
import { useParams } from 'react-router';

import { useGetRequestListQuery } from '../../../../queries/requestListQuery';
import { selectedTeamIdQuery } from '../../../../queries/teamQuery';
import { useZustand } from '../../../../zustandStore';
import type { RequestType } from '../../../../zustandStore/slices';

import RequestMenuItem from './requestMenuItem';

const moveNewRequestToFirstInListIfExists = (requests: RequestType[] | undefined) => {
	if (!Array.isArray(requests)) {
		return [] as RequestType[];
	}
	const index = requests.findIndex(item => item.state === 'CREATED');

	if (index === 0) {
		return requests;
	}

	if (index !== -1 && requests[index]) {
		return [requests[index] as RequestType, ...requests.slice(0, index), ...requests.slice(index + 1)];
	}
	return requests;
};

const RequestListMenu: FC = () => {
	const { requestId } = useParams<{ requestId: string }>();
	const { data: requests, isLoading } = useGetRequestListQuery();
	const { data: selectedTeamId } = useQuery(selectedTeamIdQuery('selectedTeamID'));
	const { setRequests } = useZustand();
	const zustandRequests = useZustand(state => state.request.requests);
	const { streamData: data } = useZustand(state => state.stream);
	const userId = useZustand(state => state.user?.userId);

	useEffect(() => {
		if (requests && requests.length > 0) {
			let updatedRequests = requests.filter(request => !request.isArchived);

			if (data && data.events) {
				data.events.forEach(event => {
					const eventRequestId = event.requestId;
					const eventTeamId = event.data.teamId;
					const eventRequester = event.data.requester?.userId;
					const exists = updatedRequests?.some(request => request.requestId === eventRequestId);
					if (!exists && eventTeamId === selectedTeamId?.[0] && eventRequester === userId) {
						updatedRequests.push(event.data as RequestType);
					}
				});
			}

			updatedRequests = updatedRequests.map(request => {
				const hasUnreadMessages = data.events?.some(event => event.requestId === request.requestId);
				return {
					...request,
					unread: hasUnreadMessages && requestId !== request.requestId,
				};
			});
			setRequests(moveNewRequestToFirstInListIfExists(updatedRequests));
		}
	}, [requests, data, requestId, selectedTeamId, userId]);

	if (isLoading) {
		return (
			<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', mt: 2 }}>
				<CircularProgress />
				<Typography sx={{ mt: 2 }} color="white">
					Loading...
				</Typography>
			</Box>
		);
	}

	if (!zustandRequests) {
		return null;
	}

	const groupedData = _.groupBy(zustandRequests, 'teamId');
	const teamRequests = groupedData[selectedTeamId?.[0] || ''] || [];

	return (
		<MenuList data-testid="my-requests-list">
			{teamRequests.length === 0 ? (
				<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', mt: 2 }}>
					<InboxIcon style={{ color: 'white' }} fontSize="large" />
					<Typography color="white">No requests to show</Typography>
				</Box>
			) : (
				teamRequests.map(request => <RequestMenuItem key={request.requestId} request={request} />)
			)}
		</MenuList>
	);
};

export default RequestListMenu;
